import { Upload, X, Image, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';


const UploadMediaModal = ({
  isDragOver,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  fileInputRef,
  handleFileSelect,
  selectedFiles,
  formatFileSize,
  removeFile,
  closeModal,
  addMedia,
  loading,
}) => {

  const { t } = useTranslation('media_gallery');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl animate-in fade-in zoom-in duration-300">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-900">{t("upload_media")}</h2>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-md hover:bg-gray-100"
          >
            <X size={24} />
          </button>
        </div>

        {/* Upload area */}
        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer mb-4 ${isDragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
            }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={`text-3xl mb-2 transition-colors duration-200 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`}>
            <Upload size={32} className="mx-auto" />
          </div>
          <div className="text-base font-medium text-gray-700 mb-1">
            {t('choose_files_to_upload')}
          </div>
          <div className="text-xs text-gray-500">{t('drag_n_drop')}</div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />

        {/* File preview */}
        {selectedFiles.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t('selected_files')} ({selectedFiles.length})
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-md relative group">
                  <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center text-gray-500 flex-shrink-0 overflow-hidden">
                    {file.type.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-full object-cover rounded"
                        onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                      />
                    ) : file.type.startsWith('video/') ? (
                      <video
                        src={URL.createObjectURL(file)}
                        className="w-full h-full object-cover rounded"
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedData={(e) => {
                          e.target.currentTime = 0.1;
                          URL.revokeObjectURL(e.target.src);
                        }}
                      />
                    ) : (
                      <Image size={14} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900 truncate">
                      {file.name.length > 15 ? file.name.substring(0, 12) + '...' : file.name}
                    </div>
                    <div className="text-xs text-gray-500">{formatFileSize(file.size)} MB</div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all duration-200 p-0.5 absolute top-1 right-1 bg-white rounded-full shadow-sm"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={closeModal}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            {t('cancel')}
          </button>
          <button
            onClick={addMedia}
            disabled={selectedFiles.length === 0 || loading}
            className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 ${selectedFiles.length > 0 && !loading
                ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                {t('upload')} {selectedFiles.length > 0 && `${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadMediaModal;
